import { RiDeleteBin6Line } from "react-icons/ri";
import { PhotoDraft, PhotoSection } from "./photoModel";
import { ProductInfo } from "./useProductValidation";
import { ItemState } from "./useUploadQueue";
import PhotoTile from "./PhotoTile";

export interface ProductGroupData {
  productKey: string;
  barcode: string;
  matCode: string;
  drafts: PhotoDraft[];
}
export interface ProductGroupProps {
  group: ProductGroupData;
  info?: ProductInfo;
  fileNames: Record<string, string>;
  conflicts: Set<string>;
  selected: Set<string>;
  uploads: Record<string, ItemState>;
  onToggleSelect(id: string): void;
  onOpen(id: string): void;
  onRetry?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDeleteGroup?: (productKey: string) => void;
}

const sectionRank = (s: PhotoSection) => (s === "cover" ? 0 : 1);

/** Border + header bg by product status — distinct color-only and with icon/text both. */
const STATUS_STYLES: Record<string, { border: string; headerBg: string }> = {
  valid:    { border: "border-emerald-200", headerBg: "bg-emerald-50/60" },
  invalid:  { border: "border-red-200",     headerBg: "bg-red-50/60" },
  checking: { border: "border-gray-200",    headerBg: "bg-gray-50/70" },
};

export default function ProductGroup({ group, info, fileNames, conflicts, selected, uploads, onToggleSelect, onOpen, onRetry, onDelete, onDeleteGroup }: ProductGroupProps) {
  const status = info?.status ?? "checking";
  const styles = STATUS_STYLES[status] ?? STATUS_STYLES.checking;
  const unknown = group.drafts.filter((d) => d.direction === null).length;
  const sorted = [...group.drafts].sort(
    (a, b) => sectionRank(a.section) - sectionRank(b.section) || a.order - b.order || a.id.localeCompare(b.id)
  );
  const keyOf = (d: PhotoDraft) => {
    const fn = fileNames[d.id];
    return fn ? `original/${d.barcode}/${d.matCode}/${d.section}/${fn}` : "";
  };

  return (
    <div className={`rounded-xl border bg-white shadow-sm overflow-hidden ${styles.border}`}>
      {/* Group header — NOT sticky; only the board bar is sticky to avoid double-sticky overlap */}
      <div className={`flex items-start justify-between gap-x-3 px-4 py-2.5 border-b border-gray-100 ${styles.headerBg}`}>
        <div className="min-w-0">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="font-semibold text-gray-800 leading-tight">{group.barcode}</span>
            <span className="text-gray-400 select-none">›</span>
            <span className="font-medium text-gray-700 leading-tight">{group.matCode}</span>
          </div>
          {/* Product name on its own line — kept as a blank line when not found so
              every card header has the same height. */}
          <div className="mt-0.5 min-h-[1.1rem] text-xs text-emerald-700 truncate" title={status === "valid" ? info?.name ?? "" : undefined}>
            {status === "valid" && info?.name ? info.name : " "}
          </div>
        </div>

        {/* Status badges — icon + text, not color alone */}
        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
          {status === "valid" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
              <span aria-hidden="true">✓</span> พบในระบบ
            </span>
          )}
          {status === "invalid" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              <span aria-hidden="true">✕</span> ไม่พบสินค้า
            </span>
          )}
          {status === "checking" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
              <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" aria-hidden="true" />
              ตรวจสอบ…
            </span>
          )}
          {unknown > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              <span aria-hidden="true">⚠</span> {unknown} ยังไม่รู้มุม
            </span>
          )}
          {onDeleteGroup && (
            <button
              type="button"
              aria-label={`ลบทั้ง matcode ${group.matCode}`}
              title="ลบทั้ง matcode นี้"
              onClick={() => onDeleteGroup(group.productKey)}
              className="inline-flex items-center justify-center rounded-md p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <RiDeleteBin6Line size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Single horizontal-scroll row of fixed-size tiles — many images (>6)
          scroll sideways instead of wrapping into a tall card. */}
      <div className="flex gap-1.5 p-3 overflow-x-auto">
        {sorted.map((d) => (
          <div key={d.id} className="w-[76px] shrink-0">
            <PhotoTile draft={d} fileName={fileNames[d.id]} selected={selected.has(d.id)}
              conflict={conflicts.has(keyOf(d))} upload={uploads[d.id]} onToggleSelect={onToggleSelect} onOpen={onOpen} onRetry={onRetry} onDelete={onDelete} />
          </div>
        ))}
      </div>
    </div>
  );
}
